/**
 * PopupDetailsModalController
 *
 * Generic controller for detail popups / modals.
 *
 * Responsibilities:
 * - Open popup via [data-popup-open]
 * - Close popup via [data-popup-close] or ESC key
 * - Support Shadow DOM & Web Components via composedPath()
 * - Lock body scroll while popup is open
 *
 * Notes:
 * - Uses event delegation (Shopify-safe)
 * - Uses capture phase to beat theme / XO handlers
 * - Does not manage focus trapping (intentional)
 */
class PopupDetailsModalController {
  constructor() {
    // Open trigger
    this.OPEN_SELECTOR = "[data-popup-open]";

    // Close trigger
    this.CLOSE_SELECTOR = "[data-popup-close]";

    // Modal element
    this.MODAL_SELECTOR = ".popup-details-modal";

    // Optional wrapper (scopes modal lookup)
    this.WRAPPER_SELECTOR = "[data-popup-wrapper]";

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  init() {
    document.addEventListener("click", this.handleClick, true);
    document.addEventListener("keydown", this.handleKeydown);

    console.log("[popup-details-modal] initialized");
  }

  /**
   * Shadow-DOM-safe element lookup
   */
  getFromPath(path, selector) {
    return path.find((el) => el instanceof HTMLElement && el.matches(selector));
  }

  handleClick(e) {
    const path = e.composedPath();

    const openBtn = this.getFromPath(path, this.OPEN_SELECTOR);
    const closeBtn = this.getFromPath(path, this.CLOSE_SELECTOR);

    // CLOSE first
    if (closeBtn) {
      e.preventDefault();
      e.stopPropagation();
      this.close(closeBtn);
      return;
    }

    // OPEN
    if (openBtn) {
      e.preventDefault();
      e.stopPropagation();
      this.open(openBtn);
    }
  }

  open(trigger) {
    const targetId = trigger.dataset.popupTarget;
    if (targetId) {
      const modal = document.getElementById(targetId);
      if (!modal) return;
      modal.hidden = false;
      document.body.style.overflow = "hidden";
      return;
    }
    const wrapper = trigger.closest(this.WRAPPER_SELECTOR);
    const modal = wrapper?.querySelector(this.MODAL_SELECTOR);
    if (!modal) return;
    modal.hidden = false;
    document.body.style.overflow = "hidden";
  }

  close(trigger) {
    const modal = trigger.closest(this.MODAL_SELECTOR);
    if (!modal) return;

    modal.hidden = true;
    document.body.style.overflow = "";
  }

  handleKeydown(e) {
    if (e.key !== "Escape") return;

    document
      .querySelectorAll(`${this.MODAL_SELECTOR}:not([hidden])`)
      .forEach((modal) => (modal.hidden = true));

    document.body.style.overflow = "";
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new PopupDetailsModalController().init();
});
